import React, { useState } from "react";
import {
  Plus,
  Trash2,
  GripVertical,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  MessageCircle,
  Eye,
  EyeOff,
  LayoutGrid,
  List,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const generateId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).substring(2, 11);
};

const SortableFAQItem = ({ faq, onUpdate, onDelete, read }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: faq.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 30 : 0,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group relative flex gap-6 bg-white border border-slate-100 rounded-[8px] p-5 hover:border-[#D4AD3015] shadow-sm transition-all duration-300"
    >
      {!read && (
        <div className="flex flex-col gap-3 pt-4">
          <div
            {...attributes}
            {...listeners}
            className="p-2.5  hover:text-primary bg-slate-50 border border-transparent hover:border-[#D4AD3015] rounded-xl cursor-grab active:cursor-grabbing transition-all"
          >
            <GripVertical size={18} />
          </div>
          <button
            type="button"
            onClick={onDelete}
            className="p-2.5 hover:text-red-500 bg-slate-50 border border-transparent hover:border-red-100 rounded-xl transition-all"
          >
            <Trash2 size={18} />
          </button>
        </div>
      )}

      <div className="flex-1 space-y-6">
        <div className="space-y-2">
          <label className="text-fs_14 md:text-fs_16 font-fw_400 capitalize">
            The Question
          </label>
          <input
            type="text"
            placeholder="Enter the Question"
            className="w-full text-fs_14 text-darkText px-3 py-2 rounded-lg lg:text-fs_16 border-[1px] border-greyText placeholder-greyText placeholder:text-fs_14 md:placeholder:text-fs_16 focus:outline-none"
            value={faq.question}
            onChange={(e) => onUpdate({ question: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <label className="text-fs_14 md:text-fs_16 font-fw_400 capitalize">
            The Answer
          </label>
          <textarea
            placeholder="Provide a clear, helpful answer..."
            className="w-full text-fs_14 text-darkText px-3 py-2 rounded-lg lg:text-fs_16 border-[1px] border-greyText placeholder-greyText placeholder:text-fs_14 md:placeholder:text-fs_16 focus:outline-none resize-none"
            value={faq.answer}
            onChange={(e) => onUpdate({ answer: e.target.value })}
          />
        </div>
      </div>
    </div>
  );
};

const FAQManager = ({ faqs, onChange, read = false }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const addFAQ = () => {
    const newItem = {
      id: generateId(),
      question: "",
      answer: "",
    };
    onChange([...faqs, newItem]);
  };

  const updateFAQ = (id, updates) => {
    onChange(faqs.map((f) => (f.id === id ? { ...f, ...updates } : f)));
  };

  const deleteFAQ = (id) => {
    onChange(faqs.filter((f) => f.id !== id));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = faqs.findIndex((i) => i.id === active.id);
      const newIndex = faqs.findIndex((i) => i.id === over.id);
      onChange(arrayMove(faqs, oldIndex, newIndex));
    }
  };

  return (
    <div className="space-y-4 animate-in fade-in slide-in-from-bottom duration-700">
      {/* FAQ Header Controls */}
      <div className="flex items-center justify-between bg-white border border-slate-200 p-4 rounded-[1rem] shadow-sm">
        <div className="flex items-center gap-6">
          <div>
            <h2 className="text-2xl font-black text-slate-900 tracking-tight">
              FAQ
            </h2>
          </div>
        </div>
        {!read && (
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={addFAQ}
              className="flex items-center gap-2 bg-primary text-white px-4 py-3.5 rounded-[12px] font-black text-xs transition-all shadow-xl shadow-violet-100"
            >
              <Plus size={16} /> Add FAQ
            </button>
          </div>
        )}
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={faqs.map((f) => f.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-6">
            {faqs.map((faq) => (
              <SortableFAQItem
                key={faq.id}
                faq={faq}
                onUpdate={(u) => updateFAQ(faq.id, u)}
                onDelete={() => deleteFAQ(faq.id)}
                read={read}
              />
            ))}

            {faqs.length === 0 && (
              <div className="py-20 bg-white border border-slate-200 rounded-[1rem] flex flex-col items-center justify-center  bg-white/40">
                <h4 className="text-fs_20 font-black tracking-tight uppercase">
                  FAQ is Empty
                </h4>
                {!read && (
                  <p className="text-slate-400 text-sm font-fw_500 mt-2">
                    Start adding common user questions to help your audience.
                  </p>
                )}
                {!read && (
                  <button
                    type="button"
                    onClick={addFAQ}
                    className="mt-10 bg-primary text-white px-10 py-4 rounded-2xl font-black text-xs transition-all shadow-2xl"
                  >
                    Create FAQ
                  </button>
                )}
              </div>
            )}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default FAQManager;
